import type { AfterViewInit, EventEmitter, OnChanges, OnDestroy, SimpleChange } from '@angular/core';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    Input,
    NgZone,
    Output,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type { Subscription } from 'rxjs';
import { BehaviorSubject, Observable } from 'rxjs';
import type { PlyrDriver } from './driver/default-plyr.driver';
import { DefaultPlyrDriver } from './driver/default-plyr.driver';
import { filter, first, switchMap } from 'rxjs/operators';
import type * as Plyr from '@sendpulse/plyr';
import { PlatformService } from '@common/services/platform.service';
import { UAParser } from 'ua-parser-js';

@Component({
    selector: 'sp-player, [spPlayer]',
    templateUrl: './sp-player.component.html',
    styleUrls: ['./sp-player.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    exportAs: 'spPlayer',
    imports: [CommonModule],
})
export class SpPlayerComponent implements AfterViewInit, OnChanges, OnDestroy {
    private readonly elementRef = inject<ElementRef<HTMLDivElement>>(ElementRef);
    private readonly ngZone = inject(NgZone);
    private readonly renderer = inject(Renderer2);
    private readonly platformService: PlatformService = inject(PlatformService);

    private playerChange$ = new BehaviorSubject<Plyr>(null);
    private events = new Map();
    private subscriptions: Subscription[] = [];
    private driver: PlyrDriver;
    private videoElement: HTMLVideoElement;

    @Input() playerType: Plyr.MediaType = 'video';
    @Input() playerTitle: string;
    @Input() playerPoster: string;
    @Input() playerSources: Plyr.Source[];
    @Input() playerTracks: Plyr.Track[];
    @Input() playerOptions: Plyr.Options;
    @Input() playerCrossOrigin: boolean;
    @Input() playerPlaysInline: boolean;
    @Input() isShorts?: boolean;
    @Input({ transform: booleanAttribute }) spPlayerLazy: boolean;

    @ViewChild('v') private vr: ElementRef;

    @Output() playerInit = this.playerChange$.pipe(filter((player: Plyr) => !!player)) as EventEmitter<Plyr>;

    // standard media events
    @Output() playerProgress = this.createLazyEvent('progress');
    @Output() playerPlaying = this.createLazyEvent('playing');
    @Output() playerPlay = this.createLazyEvent('play');
    @Output() playerPause = this.createLazyEvent('pause');
    @Output() playerTimeUpdate = this.createLazyEvent('timeupdate');
    @Output() playerVolumeChange = this.createLazyEvent('volumechange');
    @Output() playerSeeking = this.createLazyEvent('seeking');
    @Output() playerSeeked = this.createLazyEvent('seeked');
    @Output() playerRateChange = this.createLazyEvent('ratechange');
    @Output() playerEnded = this.createLazyEvent('ended');
    @Output() playerEnterFullScreen = this.createLazyEvent('enterfullscreen');
    @Output() playerExitFullScreen = this.createLazyEvent('exitfullscreen');
    @Output() playerCaptionsEnabled = this.createLazyEvent('captionsenabled');
    @Output() playerCaptionsDisabled = this.createLazyEvent('captionsdisabled');
    @Output() playerLanguageChange = this.createLazyEvent('languagechange');
    @Output() playerControlsHidden = this.createLazyEvent('controlshidden');
    @Output() playerControlsShown = this.createLazyEvent('controlsshown');
    @Output() playerReady = this.createLazyEvent('ready');

    // HTML5 events
    @Output() playerLoadStart = this.createLazyEvent('loadstart');
    @Output() playerLoadedData = this.createLazyEvent('loadeddata');
    @Output() playerLoadedMetadata = this.createLazyEvent('loadedmetadata');
    @Output() playerQualityChange = this.createLazyEvent('qualitychange');
    @Output() playerCanPlay = this.createLazyEvent('canplay');
    @Output() playerCanPlayThrough = this.createLazyEvent('canplaythrough');
    @Output() playerStalled = this.createLazyEvent('stalled');
    @Output() playerWaiting = this.createLazyEvent('waiting');
    @Output() playerEmptied = this.createLazyEvent('emptied');
    @Output() playerCueChange = this.createLazyEvent('cuechange');
    @Output() playerError = this.createLazyEvent('error');

    // YouTube events
    @Output() playerStateChange = this.createLazyEvent('statechange');

    public isBrowser: boolean = this.platformService.isPlatformBrowser();
    public isActivated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public ngOnChanges(changes: { [p in keyof SpPlayerComponent]?: SimpleChange }): void {
        this.subscriptions.push(
            this.playerInit.pipe(first()).subscribe((player: Plyr) => {
                const reInitTriggers = [
                    changes.playerOptions,
                    changes.playerPlaysInline,
                    changes.playerCrossOrigin,
                    changes.isShorts,
                ].filter((change: SimpleChange) => !!change);
                if (reInitTriggers.length && reInitTriggers.some((change: SimpleChange) => !change.firstChange)) {
                    this.initPlayer(true);
                    return;
                }

                this.updatePlayerSource(player);
            })
        );
    }

    public ngOnDestroy(): void {
        this.destroyPlayer();
        this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }

    public ngAfterViewInit(): void {
        if (!this.spPlayerLazy || this.sourceProvider === 'html5') {
            this.initPlayer();
        }
    }

    public activatePlayer(): void {
        if (this.sourceProvider === 'html5' || !this.spPlayerLazy || this.isActivated$.value) {
            return;
        }

        this.isActivated$.next(true);

        const { os } = UAParser(navigator.userAgent);
        if (os.name === 'iOS' && this.sourceProvider === 'youtube') {
            this.playerOptions = {
                ...this.playerOptions,
                muted: true,
                youtube: {
                    playsinline: 1,
                },
                fullscreen: {
                    enabled: true,
                    fallback: true,
                    iosNative: false,
                },
            };
        }
        this.initPlayer();

        this.playerInit.pipe(first()).subscribe((player: Plyr) => {
            player.on('ready', () => {
                if (os.name === 'iOS') {
                    player.muted = true;
                }

                player.play();
            });
        });
    }

    public getThumbnailUrl(): string {
        if (this.playerPoster) {
            return this.playerPoster;
        }

        if (!this.playerSources?.length) {
            return '';
        }

        const src = this.playerSources[0].src;
        const youtubeMatch = src.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);

        if (youtubeMatch && youtubeMatch[2]) {
            return `https://i.ytimg.com/vi/${youtubeMatch[2]}/maxresdefault.jpg`;
        }

        const vimeoMatch = src.match(/^.*(vimeo.com\/|video\/)(\d+).*/);

        if (vimeoMatch && vimeoMatch[2]) {
            return `https://vumbnail.com/${vimeoMatch[2]}.jpg`;
            // TODO: maybe use official api? this.http.get('http://vimeo.com/api/v2/video/1151954219.json')
        }
    }

    private initPlayer(force: boolean = false): void {
        if (force || !this.player) {
            const hasVimeoSource = this.playerSources?.some((source) => source.provider === 'vimeo');
            const clickToPlay = this.playerOptions?.clickToPlay ?? !hasVimeoSource;

            this.ngZone.runOutsideAngular(() => {
                this.destroyPlayer();
                this.driver = new DefaultPlyrDriver();
                this.ensureVideoElement();
                const newPlayer = this.driver.create({
                    videoElement: this.videoElement,
                    options: {
                        ...this.playerOptions,
                        debug: false,
                        clickToPlay,
                        ratio: this.isShorts ? '9:16' : '',
                        captions: {
                            active: true,
                            language: 'auto',
                            update: true,
                        },
                        controls: [
                            'play',
                            'progress',
                            'current-time',
                            'mute',
                            'volume',
                            'captions',
                            'settings',
                            'fullscreen',
                        ],
                        vimeo: {
                            controls: false,
                            ...(this.playerOptions?.vimeo || {}),
                        },
                        youtube: {
                            controls: 0,
                            cc_load_policy: 0,
                            ...(this.playerOptions?.youtube || {}),
                        },
                        fullscreen: {
                            enabled: true,
                            fallback: true,
                            iosNative: true,
                            ...(this.playerOptions?.fullscreen || {}),
                        },
                    } as Plyr.Options,
                });
                this.updatePlayerSource(newPlayer);
                this.playerChange$.next(newPlayer);

                //@ts-expect-error - there is no type definition for 'touch' in Plyr
                if (newPlayer?.touch || newPlayer?.provider === 'vimeo') {
                    //@ts-expect-error - there is no typed click event
                    newPlayer.on('click', (event: PointerEvent) => {
                        if ((event.target as HTMLElement).className === 'plyr__poster') {
                            newPlayer.togglePlay();
                        }
                    });
                }
            });
        }
    }

    private updatePlayerSource(player: Plyr): void {
        this.driver.updateSource({
            videoElement: this.videoElement,
            player,
            source: {
                type: this.playerType,
                title: this.playerTitle,
                sources: this.playerSources,
                poster: this.playerPoster,
                tracks: this.playerTracks,
            },
        });

        // ! do not remove this.isBrowser until investigating
        if (this.playerPoster && this.isBrowser) {
            setTimeout(() => {
                this.player.poster = this.playerPoster;
            }, 200);
        }
    }

    private createLazyEvent<T extends Plyr.PlyrEvent>(
        name: Plyr.StandardEvent | Plyr.Html5Event | Plyr.YoutubeEvent
    ): EventEmitter<T> {
        return this.playerInit.pipe(
            switchMap(
                () =>
                    new Observable((observer) => this.on(name, (data: T) => this.ngZone.run(() => observer.next(data))))
            )
        ) as EventEmitter<T>;
    }

    private destroyPlayer(): void {
        if (!this.player) {
            return;
        }

        Array.from(this.events.keys()).forEach((name: string) => this.off(name));

        this.driver.destroy({
            player: this.player,
        });

        this.videoElement = null;
    }

    private get hostElement(): HTMLDivElement {
        return this.elementRef.nativeElement;
    }

    // this method is required because the player inserts clone of the original element on destroy
    // so we catch the clone element right here and reuse it
    private ensureVideoElement(): void {
        const videoElement = this.hostElement.querySelector('video');

        if (videoElement) {
            this.videoElement = videoElement;
            return;
        }

        this.videoElement = this.renderer.createElement('video');
        this.videoElement.controls = true;

        if (this.playerCrossOrigin) {
            this.videoElement.setAttribute('crossorigin', '');
        }

        if (this.playerPlaysInline) {
            this.videoElement.setAttribute('playsinline', '');
        }

        this.renderer.appendChild(this.hostElement, this.videoElement);
    }

    private on(name: string, handler: any): void {
        this.events.set(name, handler);
        this.player.on(name as any, handler);
    }

    private off(name: string): void {
        this.player.off(name as any, this.events.get(name) as any);
        this.events.delete(name);
    }

    public get player(): Plyr {
        return this.playerChange$.getValue();
    }

    public get sourceProvider(): Plyr.Provider {
        return this.playerSources?.[0].provider;
    }
}
